import AxiosService from "../axios-service/AxiosService";
import Storage from "../../services/storage-service/Storage";
import ErrorHandler from "../../utils/ErrorHandler";
import Validate from "../../services/validate-service/Validate";

const authServerUrl = process.env.REACT_APP_AUTH_URL;
export const PERMISSIONS = {
    UPDATE_PAYMENT_CREDENTIAL: "write_miniapp_credential"
};

export default class AuthService {
    static isAuthenticated = () => {
        try {
            const token = AuthService.getDecodedToken();
            if (!token) {
                console.log("Sem token, saindo do admin...");
                AuthService.signOut();
            }

            const isObject = typeof token === "object";
            const isValidSchemas = Validate.hasProperties(token, ["email"]);
            const isValidEmail = Validate.emailValidate(token.email);
            const isScopesArray = Array.isArray(token.scopes);
            const isExpNumber = typeof token.exp === "number";
            const isExpiredToken = AuthService.isExpiredToken(token);

            if (
                isObject &&
                isValidSchemas &&
                isValidEmail &&
                isScopesArray &&
                isExpNumber &&
                !isExpiredToken
            ) {
                return true;
            }
            return false;
        } catch (erro) {
            AuthService.signOut();
        }
        return false;
    };

    static signOut = () => {
        Storage.clearAll();
    };

    static getDecodedToken = () => {
        const token = Storage.get(Storage.KEYS.USERTOKEN);
        if (!token) return null;
        return JSON.parse(atob(token.split(".")[1]));
    };

    static isAdmin = () => AuthService.getTokenRole() === "ADMIN";

    static getTokenRole = () => {
        return AuthService.getDecodedToken() &&
            AuthService.getDecodedToken().role
            ? AuthService.getDecodedToken().role.toUpperCase()
            : null;
    };

    static basicAuth = (username, password) => {
        const user = { auth: { username, password } };

        return AxiosService.httpPost(authServerUrl, {}, user)
            .then(response => {
                this.saveTokenOnLocalStorage(response.data.accessToken);
                return response;
            })
            .catch(err => {
                ErrorHandler.handleError(err);
            });
    };

    static azureAuth = async idToken => {
        return await AxiosService.httpPost(authServerUrl + "/azure", {
            idToken
        });
    };

    static saveTokenOnLocalStorage = token => {
        Storage.set(Storage.KEYS.USERTOKEN, token);
    };

    static hasUpdatePaymentCredentialPermission = () => {
        const token = AuthService.getDecodedToken();
        const isScopesArray = token ? Array.isArray(token.scopes) : null;
        if (!isScopesArray) {
            return false;
        }

        return !!token.scopes.find(
            scope => scope === PERMISSIONS.UPDATE_PAYMENT_CREDENTIAL
        );
    };

    static isFirstLogin = () => {
        const decodedToken = AuthService.getDecodedToken();
        return decodedToken.redefinepwd;
    };

    static isExpiredToken = token => {
        const now = Date.now();
        const tokenExpiringDate = token.exp * 1000;
        return now > tokenExpiringDate;
    };

    static createPassword = async keys => {
        let token = null;
        try {
            token = Storage.get(Storage.KEYS.USERTOKEN);
        } catch (e) {
            ErrorHandler.handleError(e);
        }

        const config = {
            headers: { Authorization: "Bearer " + token }
        };

        return await AxiosService.httpPut(authServerUrl, keys, config);
    };

    static resetPassword = async (token, password) => {
        return await AxiosService.httpPost(`${authServerUrl}/reset`, {
            token,
            password
        });
    };

    static findAccount = async email => {
        return await AxiosService.httpPost(`${authServerUrl}/find/account`, {
            email
        });
    };

    static hasAllScopes = scopes => {
        if (!scopes) return false;
        if (scopes.length < 1) return false;

        const resultArray = scopes.map(scope => {
            if (!AuthService.hasScope(scope)) return false;
        });

        return !resultArray.includes(false);
    };

    static hasScope = scope => {
        const token = AuthService.getDecodedToken();
        if (!token) {
            console.log("Sem token, saindo do admin...");
            AuthService.signOut();
            return false;
        }
        return token.scopes && token.scopes.find(s => s === scope);
    };

    static changePassword = async (newPassword, currentPassword) => {
        let token = null;
        try {
            token = Storage.get(Storage.KEYS.USERTOKEN);
        } catch (e) {
            ErrorHandler.handleError(e);
        }
        const config = {
            headers: {
                Authorization: `Bearer ${token}`
            }
        };
        return await AxiosService.httpPut(
            `${authServerUrl}`,
            { newPassword, currentPassword },
            config
        );
    };
}
